import { Badge, Divider, Grid, Group, Space, Stack, Text } from '@mantine/core';
import { BusinessModeOfPayments } from '../../../enums/business-mode-payments';
import { BusinessTypes, BusinessTypesText } from '../../../enums/business-type';
import { Currency } from '../../../enums/retrieve-transaction.enums';
import { formatDateWithTime } from '../../../helpers/date-convert';
import { GeneralOnboardingInformation } from '../../../type/admin.types';
import { TextInfo } from '../../safe-text/safe-text.component';
import XInfoTabTitle from '../../x-components/x-info-tab-title.component';

interface MerchantInfoGeneralProps {
  generalData?: GeneralOnboardingInformation;
  parseLink: (link: string) => string;
}
function MerchantInfoGeneral(props: MerchantInfoGeneralProps) {
  const { generalData, parseLink } = props;
  const labels = {
    businessType: 'Business type',

    merchantName: 'Company name',
    merchantBusinessName: 'Registered business name',
    businessHandle: 'Name for payment link',
    merchantCompanyTin: 'Company TIN',

    mainOfficeContactNumber: 'Main office contact number',
    mainOfficeBldgNameNo: 'Main office building name and number',
    mainOfficeStreetAddress: 'Main office street address',
    mainOfficeBarangay: 'Main office barangay',
    mainOfficeCity: 'Main office city',
    mainOfficeProvince: 'Main office province',
    mainOfficePostalCode: 'Main office postal code',

    registeredContactNumber: 'Registered contact number',
    registeredBldgNameNo: 'Registered building name and number',
    registeredStreetAddress: 'Registered street address',
    registeredBarangay: 'Registered barangay',
    registeredCity: 'Registered city',
    registeredProvince: 'Registered province',
    registeredPostalCode: 'Registered postal code',

    isBrickAndMortarStore: 'Do you have a physical store?',
    industry: 'Industry',
    serviceDescription: 'Service description',
    modeOfPayment: 'Mode of Payment/s',
    currency: 'Currency',
    estimatedMonthlyGrossTransaction: 'Estimated Monthly Gross Transaction',

    aubPartnerId: 'AUB Partner ID',
    acquirer: 'Acquirer',
  };

  const initialMop = generalData?.modeOfPaymentId.sort((a, b) => {
    return a - b;
  });

  const mop = initialMop?.map((mopId) => {
    let text = '';
    if (mopId === BusinessModeOfPayments.TERMINAL) {
      text = 'TERMINAL';
    } else if (mopId === BusinessModeOfPayments.ECOM) {
      text = 'ECOM';
    } else if (mopId === BusinessModeOfPayments.PAYMENT_LINK) {
      text = 'PAYMENT LINK';
    } else if (mopId === BusinessModeOfPayments.QRPH) {
      text = 'QRPH';
    } else if (mopId === BusinessModeOfPayments.PAY_WITH_MAYA) {
      text = 'PAY WITH MAYA';
    } else {
      text = 'Unknown mode of payment';
    }
    return <Badge mr={3}>{text}</Badge>;
  });

  return (
    <>
      <Group grow>
        <XInfoTabTitle
          label='General Information'
          dataCy='generalInformationTitle'
        />
      </Group>
      <Space h='xl' />
      <Grid>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.businessType}
            value={
              generalData?.businessTypeId === BusinessTypes.STARTER
                ? BusinessTypesText.STARTER
                : generalData?.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
                ? BusinessTypesText.SOLE_PROPRIETOR
                : generalData?.businessTypeId === BusinessTypes.PARTNERSHIP
                ? BusinessTypesText.PARTNERSHIP
                : generalData?.businessTypeId === BusinessTypes.CORPORATION
                ? BusinessTypesText.CORPORATION
                : '.'
            }
            dataCy='businessTypeId'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.merchantBusinessName}
            value={
              generalData?.businessTypeId === BusinessTypes.STARTER
                ? '.'
                : generalData?.legalName
            }
            dataCy='legalName'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.merchantName}
            value={generalData?.merchantName}
            dataCy='merchantName'
            breakWord
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.businessHandle}
            value={generalData?.businessHandle}
            dataCy='businessHandle'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.merchantCompanyTin}
            value={generalData?.tin}
            dataCy='companyTin'
          />
        </Grid.Col>

        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.isBrickAndMortarStore}
            value={
              generalData?.isBrickAndMortarStore === true
                ? 'Yes'
                : generalData?.isBrickAndMortarStore === false
                ? 'No'
                : '.'
            }
            dataCy='isBrickAndMortarStore'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.industry}
            value={generalData?.industry}
            dataCy='industry'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <Stack spacing='xs'>
            <Text size='sm' weight={600}>
              {labels.modeOfPayment}
            </Text>
            <Text size='sm' data-cy='modeOfPaymentId'>
              {mop}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.currency}
            value={
              generalData?.currencyId === Currency.PHP
                ? 'PHP'
                : generalData?.currencyId === Currency.USD
                ? 'USD'
                : '.'
            }
            dataCy='currency'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.estimatedMonthlyGrossTransaction}
            value={generalData?.estimatedMonthlyGrossTransaction}
            dataCy='estimatedMonthlyGrossTransaction'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.aubPartnerId}
            value={generalData?.aubPartnerId}
            dataCy='aubPartnerId'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <Stack spacing='xs'>
            <Text size='sm' weight={600}>
              {labels.acquirer}
            </Text>
            <Text size='sm'>
              {generalData?.acquirers?.map((acquirer) => {
                return <Badge mr={3}>{acquirer.type}</Badge>;
              })}
            </Text>
          </Stack>
        </Grid.Col>
        <Grid.Col xs={12}>
          <TextInfo
            label={labels.serviceDescription}
            value={generalData?.serviceDescription}
            dataCy='serviceDescription'
          />
        </Grid.Col>
      </Grid>

      <Divider my='xl' />

      <Text size='xl' weight={700}>
        Main Office Contact Information
      </Text>
      <Space h='xl' />

      <Grid data-cy='mainOfficeContactInformation'>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.mainOfficeContactNumber}
            value={
              generalData?.address.office.contactNumber
                ? '+63 ' + generalData?.address.office.contactNumber
                : '.'
            }
            dataCy='contactNumber'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.mainOfficeCity}
            value={generalData?.address.office.city}
            dataCy='city'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.mainOfficeBldgNameNo}
            value={
              !generalData?.address.office.buildingNameAndNumber
                ? '.'
                : generalData?.address.office.buildingNameAndNumber
            }
            dataCy='buildingNameAndNumber'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.mainOfficeBarangay}
            value={generalData?.address.office.barangay}
            dataCy='barangay'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.mainOfficeStreetAddress}
            value={generalData?.address.office.line1}
            dataCy='line1'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.mainOfficePostalCode}
            value={generalData?.address.office.postalCode}
            dataCy='postalCode'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <TextInfo
            label={labels.mainOfficeProvince}
            value={generalData?.address.office.state}
            dataCy='state'
          />
        </Grid.Col>
      </Grid>
      <Divider my='xl' />

      {generalData?.businessTypeId !== BusinessTypes.STARTER && (
        <>
          <Text size='xl' weight={700}>
            Registered Contact Information
          </Text>
          <Space h='xl' />

          <Grid data-cy='registeredContactInformation'>
            <Grid.Col md={3} sm={6}>
              <TextInfo
                label={labels.registeredContactNumber}
                value={
                  !generalData?.address?.registered?.contactNumber
                    ? '.'
                    : '+63 ' + generalData?.address?.registered?.contactNumber
                }
                dataCy='contactNumber'
              />
            </Grid.Col>
            <Grid.Col md={3} sm={6}>
              <TextInfo
                label={labels.registeredCity}
                value={generalData?.address?.registered?.city}
                dataCy='city'
              />
            </Grid.Col>
            <Grid.Col md={3} sm={6}>
              <TextInfo
                label={labels.registeredBldgNameNo}
                value={
                  !generalData?.address?.registered?.buildingNameAndNumber
                    ? '.'
                    : generalData?.address?.registered?.buildingNameAndNumber
                }
                dataCy='buildingNameAndNumber'
              />
            </Grid.Col>
            <Grid.Col md={3} sm={6}>
              <TextInfo
                label={labels.registeredBarangay}
                value={generalData?.address?.registered?.barangay}
                dataCy='barangay'
              />
            </Grid.Col>
            <Grid.Col md={3} sm={6}>
              <TextInfo
                label={labels.registeredStreetAddress}
                value={generalData?.address?.registered?.line1}
                dataCy='line1'
              />
            </Grid.Col>
            <Grid.Col md={3} sm={6}>
              <TextInfo
                label={labels.registeredPostalCode}
                value={generalData?.address?.registered?.postalCode}
                dataCy='postalCode'
              />
            </Grid.Col>
            <Grid.Col md={3} sm={6}>
              <TextInfo
                label={labels.registeredProvince}
                value={generalData?.address?.registered?.state}
                dataCy='state'
              />
            </Grid.Col>
          </Grid>
          <Divider my='xl' />
        </>
      )}

      <Text size='xl' weight={700}>
        Websites
      </Text>
      <Space h='xl' />
      <Grid data-cy='websites'>
        <Grid.Col md={3}>
          <Stack>
            <Text size='sm' weight={600}>
              Business Website
            </Text>
            {generalData?.websites?.businessWebsiteUrl ? (
              <Text
                variant='link'
                component='a'
                href={parseLink(generalData?.websites?.businessWebsiteUrl)}
                target='_blank'
                size='sm'
                data-cy='businessWebsiteUrl'
              >
                {generalData?.websites?.businessWebsiteUrl}
              </Text>
            ) : (
              <Text size='sm' color='dimmed' data-cy='businessWebsiteUrl'>
                N/A
              </Text>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col md={9}>
          <TextInfo
            label='Business Website Under Development'
            value={
              generalData?.websites?.isBusinessWebsiteUnderDevelopment === true
                ? 'Yes'
                : generalData?.websites?.isBusinessWebsiteUnderDevelopment ===
                  false
                ? 'No'
                : '.'
            }
            dataCy='isBusinessWebsiteUnderDevelopment'
          />
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <Stack spacing='xs'>
            <Text size='sm' weight={600}>
              Facebook
            </Text>
            {generalData?.websites?.facebookUrl ? (
              <Text
                variant='link'
                component='a'
                href={parseLink(generalData?.websites?.facebookUrl)}
                target='_blank'
                size='sm'
                data-cy='facebookUrl'
              >
                {generalData?.websites?.facebookUrl}
              </Text>
            ) : (
              <Text size='sm' color='dimmed' data-cy='facebookUrl'>
                N/A
              </Text>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <Stack spacing='xs'>
            <Text size='sm' weight={600}>
              Twitter
            </Text>
            {generalData?.websites?.twitterUrl ? (
              <Text
                variant='link'
                component='a'
                href={parseLink(generalData?.websites?.twitterUrl)}
                target='_blank'
                size='sm'
                data-cy='twitterUrl'
              >
                {generalData?.websites?.twitterUrl}
              </Text>
            ) : (
              <Text size='sm' color='dimmed' data-cy='twitterUrl'>
                N/A
              </Text>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <Stack spacing='xs'>
            <Text size='sm' weight={600}>
              Instagram
            </Text>
            {generalData?.websites?.instagramUrl ? (
              <Text
                variant='link'
                component='a'
                href={parseLink(generalData?.websites?.instagramUrl)}
                target='_blank'
                size='sm'
                data-cy='instagramUrl'
              >
                {generalData?.websites?.instagramUrl}
              </Text>
            ) : (
              <Text size='sm' color='dimmed' data-cy='instagramUrl'>
                N/A
              </Text>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col md={3} sm={6}>
          <Stack spacing='xs'>
            <Text size='sm' weight={600}>
              Tiktok
            </Text>
            {generalData?.websites?.tiktokUrl ? (
              <Text
                variant='link'
                component='a'
                href={parseLink(generalData?.websites?.tiktokUrl)}
                target='_blank'
                size='sm'
                data-cy='tiktokUrl'
              >
                {generalData?.websites?.tiktokUrl}
              </Text>
            ) : (
              <Text size='sm' color='dimmed' data-cy='tiktokUrl'>
                N/A
              </Text>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      <Divider my='xl' />

      <Space h='xl' />

      <Grid>
        <Grid.Col>
          <TextInfo
            orientation='horizontal'
            label='Onboarding reference number:'
            value={generalData?.onboardingReferenceNumber}
            dataCy='onboardingRefNumber'
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInfo
            orientation='horizontal'
            label='Date Created:'
            value={
              generalData?.dateCreated
                ? formatDateWithTime(generalData?.dateCreated.toString())
                : ''
            }
            dataCy='dateCreated'
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Group>
            {generalData?.onboardingStatus !== 'NO_RECORD' &&
              (generalData?.onboardingStatus === 'APPROVED' &&
              generalData?.dateApproved ? (
                <TextInfo
                  orientation='horizontal'
                  label={'Date Approved:'}
                  value={formatDateWithTime(
                    generalData?.dateApproved.toString()
                  )}
                />
              ) : generalData?.onboardingStatus === 'DECLINED' &&
                generalData?.dateDeclined ? (
                <TextInfo
                  orientation='horizontal'
                  label={'Date Declined:'}
                  value={formatDateWithTime(
                    generalData?.dateDeclined.toString()
                  )}
                />
              ) : generalData?.onboardingStatus === 'PENDING' &&
                generalData?.datePending ? (
                <TextInfo
                  orientation='horizontal'
                  label={'Date Pending:'}
                  value={formatDateWithTime(
                    generalData?.datePending.toString()
                  )}
                />
              ) : (
                ''
              ))}
          </Group>
        </Grid.Col>
      </Grid>
    </>
  );
}

export default MerchantInfoGeneral;
